import type { PageHelp } from '../../help/types';

/** Page guide for `/projects/:code/missing` — the inbox development builds fill (spec §9.3, §9.5, §12). */
export const missingKeysHelp: PageHelp = {
  title: 'Missing keys',
  intro:
    'Keys a development build asked for and did not find, reported through i18next’s saveMissing handler. Each report is either turned into a real key here or ignored.',
  sections: ['Reading the inbox', 'Acting on a report', 'Create key dialog'],
  contexts: {
    'create-dialog': 'Click “Create key” on an open report.',
  },
  tips: [
    'Only keys that do not exist land here. A key that exists but has no value in the reported locale is closed as created on arrival: it is a missing translation for the Keys screen, not for this inbox.',
    '“Create key” and “Ignore” need the owner or developer role on the project, or an administrator; for everyone else they are disabled and a note above the list says so.',
    'A key this project links from another project counts as existing: a report for it closes on arrival like one for the project’s own key.',
  ],
  steps: [
    {
      selector: '[data-help="missing-filters"]',
      title: 'Read the report count',
      body: 'The number on the right is how many reports have the selected status. The table lists them most recently seen first, 100 at a time. A report is one key in one locale, so a key missing in two languages counts twice.',
      section: 'Reading the inbox',
      i18n: {
        'de-AT': {
          title: 'Anzahl der Meldungen lesen',
          body: 'Die Zahl rechts gibt an, wie viele Meldungen den gewählten Status haben. Die Tabelle zeigt sie nach letzter Sichtung, die neuesten zuerst, jeweils 100 auf einmal. Eine Meldung ist ein Schlüssel in einer Sprache, ein Schlüssel, der in zwei Sprachen fehlt, zählt also doppelt.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje broja prijava',
          body: 'Broj desno pokazuje koliko prijava ima izabrani status. Tabela ih prikazuje po poslednjem viđenju, najnovije prve, po 100 odjednom. Jedna prijava je jedan ključ na jednom jeziku, pa se ključ koji nedostaje na dva jezika broji dvaput.',
        },
      },
    },
    {
      selector: '[data-help="missing-status"]',
      title: 'Switch between open, created and ignored',
      body: 'Open is the inbox. Created and ignored are kept so a decision can be looked up later, and so the same key reported again does not come back as new: a new sighting of an ignored key only raises its seen count. Created also holds the reports that closed themselves because the key already existed.',
      section: 'Reading the inbox',
      i18n: {
        'de-AT': {
          title: 'Zwischen offen, angelegt und ignoriert wechseln',
          body: 'Open ist der Eingang. Created und Ignored bleiben erhalten, damit sich eine Entscheidung später nachlesen lässt und derselbe Schlüssel bei erneuter Meldung nicht wieder als neu erscheint: Eine neue Sichtung eines ignorierten Schlüssels erhöht nur seinen Zähler. Created enthält auch Meldungen, die sich selbst geschlossen haben, weil der Schlüssel schon existierte.',
        },
        'sr-Latn-RS': {
          title: 'Prebacivanje između otvorenih, kreiranih i ignorisanih',
          body: 'Open je prijemno sanduče. Created i Ignored se čuvaju da bi se odluka kasnije mogla proveriti i da se isti ključ, kada ponovo bude prijavljen, ne bi vratio kao nov: novo viđenje ignorisanog ključa samo povećava njegov brojač. Created sadrži i prijave koje su se same zatvorile jer je ključ već postojao.',
        },
      },
    },
    {
      selector: '[data-help="missing-list"]',
      title: 'Read a report',
      body: 'Fallback shown is what the application rendered instead of a translation, usually the English text, which makes a good source value. Route is the page it was seen on, Seen counts the sightings, and Last seen names the environment of the latest one.',
      section: 'Reading the inbox',
      i18n: {
        'de-AT': {
          title: 'Eine Meldung lesen',
          body: '„Fallback shown“ ist das, was die Anwendung statt einer Übersetzung angezeigt hat, meist der englische Text, der einen guten Ausgangswert abgibt. „Route“ ist die Seite, auf der er auftrat, „Seen“ zählt die Sichtungen, und „Last seen“ nennt die Umgebung der letzten.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje prijave',
          body: '„Fallback shown“ je ono što je aplikacija prikazala umesto prevoda, obično engleski tekst, koji je dobra izvorna vrednost. „Route“ je stranica na kojoj je viđen, „Seen“ broji viđenja, a „Last seen“ navodi okruženje poslednjeg.',
        },
      },
    },
    {
      selector: '[data-help="missing-load-more"]',
      title: 'Load more reports',
      body: 'Shown under the table while reports with the selected status are not all loaded yet. Each click adds the next 100 to the table; the button says how many it will fetch and disappears once everything is shown.',
      section: 'Reading the inbox',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Weitere Meldungen laden',
          body: 'Erscheint unter der Tabelle, solange noch nicht alle Meldungen mit dem gewählten Status geladen sind. Jeder Klick fügt die nächsten 100 hinzu; die Schaltfläche nennt die Anzahl, die sie lädt, und verschwindet, sobald alles angezeigt wird.',
        },
        'sr-Latn-RS': {
          title: 'Učitavanje još prijava',
          body: 'Prikazuje se ispod tabele dok sve prijave sa izabranim statusom još nisu učitane. Svaki klik dodaje sledećih 100; dugme navodi koliko će ih učitati i nestaje kada je sve prikazano.',
        },
      },
    },
    {
      selector: '[data-help="missing-create"]',
      title: 'Create the missing key',
      body: 'Opens a dialog that creates the key in this project, with the fallback as its source value, and closes the report. Shown only on open reports, and disabled without the owner or developer role.',
      section: 'Acting on a report',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Fehlenden Schlüssel anlegen',
          body: 'Öffnet einen Dialog, der den Schlüssel in diesem Projekt mit dem Ersatzwert als Ausgangswert anlegt und die Meldung schließt. Nur bei offenen Meldungen sichtbar und ohne die Rolle Owner oder Developer gesperrt.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje ključa koji nedostaje',
          body: 'Otvara dijalog koji kreira ključ u ovom projektu, sa rezervnim tekstom kao izvornom vrednošću, i zatvara prijavu. Prikazuje se samo kod otvorenih prijava i zaključano je bez uloge owner ili developer.',
        },
      },
    },
    {
      selector: '[data-help="missing-link"]',
      title: 'Use the key another project has',
      body: 'Shown instead of “Create key” when another project already has a key of that name — the application is asking for that project’s key. One click links it: the report closes, and this project exports the other project’s text from the next publish on, in the locales that project carries. The key stays with its owner, who keeps translating it; Linked keys lists it from then on. For a plural reported as name_one, the key name without the suffix is linked and the other forms close with it.',
      section: 'Acting on a report',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Schlüssel eines anderen Projekts verwenden',
          body: 'Erscheint statt „Create key“, wenn ein anderes Projekt bereits einen Schlüssel mit diesem Namen hat – die Anwendung fragt dann nach dem Schlüssel jenes Projekts. Ein Klick verknüpft ihn: Die Meldung wird geschlossen, und dieses Projekt exportiert ab dem nächsten Veröffentlichen den Text des anderen Projekts, in den Sprachen, die jenes Projekt führt. Der Schlüssel bleibt bei seinem Owner, der ihn weiter übersetzt; unter „Linked keys“ ist er ab dann aufgeführt. Bei einem Plural, der als name_one gemeldet wurde, wird der Name ohne Endung verknüpft, und die übrigen Formen werden mit ihm geschlossen.',
        },
        'sr-Latn-RS': {
          title: 'Korišćenje ključa koji ima drugi projekat',
          body: 'Prikazuje se umesto „Create key“ kada drugi projekat već ima ključ tog imena – aplikacija tada traži ključ tog projekta. Jedan klik ga povezuje: prijava se zatvara, a ovaj projekat od sledeće objave izvozi tekst drugog projekta, na jezicima koje taj projekat nosi. Ključ ostaje kod vlasnika, koji ga i dalje prevodi; od tada je naveden pod „Linked keys“. Kod množine prijavljene kao name_one povezuje se ime bez nastavka, a ostali oblici se zatvaraju zajedno sa njim.',
        },
      },
    },
    {
      selector: '[data-help="missing-ignore"]',
      title: 'Ignore a report',
      body: 'For keys that were a mistake in the code. The report moves to Ignored at once, without a confirmation, and ignored reports are deleted after 90 days.',
      section: 'Acting on a report',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Meldung ignorieren',
          body: 'Für Schlüssel, die ein Fehler im Code waren. Die Meldung wandert sofort und ohne Rückfrage zu Ignored, und ignorierte Meldungen werden nach 90 Tagen gelöscht.',
        },
        'sr-Latn-RS': {
          title: 'Ignorisanje prijave',
          body: 'Za ključeve koji su bili greška u kodu. Prijava odmah prelazi u Ignored, bez potvrde, a ignorisane prijave se brišu posle 90 dana.',
        },
      },
    },
    {
      selector: '[data-help="missing-create-key"]',
      title: 'Check the key name',
      body: 'The key that will be created. i18next reports the plural form it resolved, such as `x_one`, but a plural suffix cannot be part of a key name: the dialog then creates the plural key `x`, stores the value as its `one` form and also closes the reports for the other forms of the same string.',
      section: 'Create key dialog',
      context: 'create-dialog',
      i18n: {
        'de-AT': {
          title: 'Schlüsselnamen prüfen',
          body: 'Der Schlüssel, der angelegt wird. i18next meldet die aufgelöste Pluralform, etwa `x_one`, ein Pluralsuffix darf aber nicht Teil eines Schlüsselnamens sein: Der Dialog legt dann den Pluralschlüssel `x` an, speichert den Wert als dessen Form `one` und schließt auch die Meldungen für die anderen Formen desselben Textes.',
        },
        'sr-Latn-RS': {
          title: 'Provera imena ključa',
          body: 'Ključ koji će biti kreiran. i18next prijavljuje oblik množine koji je razrešio, na primer `x_one`, ali sufiks množine ne može biti deo imena ključa: dijalog tada kreira ključ sa množinom `x`, čuva vrednost kao njegov oblik `one` i zatvara i prijave za ostale oblike istog teksta.',
        },
      },
    },
    {
      selector: '[data-help="missing-create-source"]',
      title: 'Set the source value',
      body: 'Prefilled with the fallback the application showed; correct it if that was not the intended text. Clearing the field does not leave the key empty: the reported fallback is used instead.',
      section: 'Create key dialog',
      context: 'create-dialog',
      i18n: {
        'de-AT': {
          title: 'Ausgangswert festlegen',
          body: 'Vorbefüllt mit dem Ersatzwert, den die Anwendung angezeigt hat; korrigieren Sie ihn, wenn das nicht der gewünschte Text war. Ein geleertes Feld lässt den Schlüssel nicht leer: Dann wird der gemeldete Ersatzwert verwendet.',
        },
        'sr-Latn-RS': {
          title: 'Unos izvorne vrednosti',
          body: 'Unapred popunjeno rezervnim tekstom koji je aplikacija prikazala; ispravite ga ako to nije željeni tekst. Ako ispraznite polje, ključ ne ostaje prazan: koristi se prijavljeni rezervni tekst.',
        },
      },
    },
    {
      selector: '[data-help="missing-create-description"]',
      title: 'Describe the key',
      body: 'Optional: what the string is for and where it appears. The hint below the field names the route the key was reported from, which is often worth copying in.',
      section: 'Create key dialog',
      context: 'create-dialog',
      i18n: {
        'de-AT': {
          title: 'Schlüssel beschreiben',
          body: 'Optional: wofür der Text ist und wo er erscheint. Der Hinweis unter dem Feld nennt die Route, von der der Schlüssel gemeldet wurde; es lohnt sich oft, sie zu übernehmen.',
        },
        'sr-Latn-RS': {
          title: 'Opis ključa',
          body: 'Opciono: čemu tekst služi i gde se pojavljuje. Napomena ispod polja navodi rutu sa koje je ključ prijavljen, koju se često isplati prepisati.',
        },
      },
    },
    {
      selector: '[data-help="missing-create-submit"]',
      title: 'Create the key',
      body: 'Creates the key in this project, so its first name segment must be a prefix this project owns. An unregistered prefix is refused until it is registered in project settings, and a prefix a package or another project owns is refused too. If the key exists by now, the report is simply closed against it.',
      section: 'Create key dialog',
      context: 'create-dialog',
      i18n: {
        'de-AT': {
          title: 'Schlüssel anlegen',
          body: 'Legt den Schlüssel in diesem Projekt an; sein erstes Namenssegment muss daher ein Präfix sein, das diesem Projekt gehört. Ein nicht registriertes Präfix wird abgelehnt, bis es in den Projekteinstellungen registriert ist, und ein Präfix, das einem Paket oder einem anderen Projekt gehört, ebenfalls. Existiert der Schlüssel inzwischen, wird die Meldung einfach mit ihm geschlossen.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje ključa',
          body: 'Kreira ključ u ovom projektu, pa prvi segment njegovog imena mora biti prefiks koji pripada ovom projektu. Neregistrovan prefiks biva odbijen dok se ne registruje u podešavanjima projekta, a odbija se i prefiks koji pripada paketu ili drugom projektu. Ako ključ u međuvremenu postoji, prijava se jednostavno zatvara uz njega.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Fehlende Schlüssel',
      intro:
        'Schlüssel, die ein Entwicklungs-Build angefordert und nicht gefunden hat, gemeldet über den saveMissing-Handler von i18next. Jede Meldung wird hier entweder zu einem echten Schlüssel oder ignoriert.',
      sections: {
        'Reading the inbox': 'Den Eingang lesen',
        'Acting on a report': 'Eine Meldung bearbeiten',
        'Create key dialog': 'Dialog „Create key“',
      },
      contexts: {
        'create-dialog': 'Klicken Sie bei einer offenen Meldung auf „Create key“.',
      },
      tips: [
        'Hier landen nur Schlüssel, die nicht existieren. Ein Schlüssel, der existiert, aber in der gemeldeten Sprache keinen Wert hat, wird beim Eintreffen als angelegt geschlossen: Das ist eine fehlende Übersetzung für die Seite „Keys“, nicht für diesen Eingang.',
        '„Create key“ und „Ignore“ erfordern die Rolle Owner oder Developer im Projekt oder Administratorrechte; für alle anderen sind sie gesperrt, und ein Hinweis über der Liste sagt das.',
        'Ein Schlüssel, den dieses Projekt aus einem anderen Projekt verknüpft, gilt als vorhanden: Eine Meldung dafür wird beim Eintreffen geschlossen wie eine für einen eigenen Schlüssel des Projekts.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Ključevi koji nedostaju',
      intro:
        'Ključevi koje je razvojni build tražio i nije pronašao, prijavljeni preko i18next saveMissing handlera. Svaka prijava se ovde ili pretvara u pravi ključ ili ignoriše.',
      sections: {
        'Reading the inbox': 'Čitanje prijemnog sandučeta',
        'Acting on a report': 'Postupanje po prijavi',
        'Create key dialog': 'Dijalog „Create key“',
      },
      contexts: {
        'create-dialog': 'Kliknite na „Create key“ kod otvorene prijave.',
      },
      tips: [
        'Ovde stižu samo ključevi koji ne postoje. Ključ koji postoji, ali nema vrednost na prijavljenom jeziku, zatvara se kao kreiran čim stigne: to je prevod koji nedostaje za ekran „Keys“, a ne za ovo sanduče.',
        '„Create key“ i „Ignore“ zahtevaju ulogu owner ili developer na projektu ili administratorska prava; za sve ostale su zaključani, a napomena iznad liste to navodi.',
        'Ključ koji ovaj projekat povezuje iz drugog projekta smatra se postojećim: prijava za njega se zatvara čim stigne, kao i za sopstveni ključ projekta.',
      ],
    },
  },
};
